import { Controller } from "@hotwired/stimulus";
import { Html5Qrcode } from "html5-qrcode";

// Connects to data-controller="qr-scanner"
export default class extends Controller {
  static targets = [
    "reader",
    "button",
    "file",
    "scanner",
    "result",
    "resultName",
    "resultDistrict",
    "error",
    "attendanceButton",
  ];
  static values = { url: String };

  connect() {
    this.scanner = null;
    this.running = false;
    this.observeDialog();
  }

  observeDialog() {
    this.dialog = this.element.closest("dialog");
    if (!this.dialog) return;

    this.observer = new MutationObserver(() => {
      if (!this.dialog.hasAttribute("open")) {
        if (this.running) this.stopScanner();
        this.reset();
      }
    });

    this.observer.observe(this.dialog, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  toggle() {
    this.running ? this.stopScanner() : this.startScanner();
  }

  startScanner() {
    this.scanner = new Html5Qrcode(this.readerTarget.id);

    this.scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        async (decodedText) => {
          await this.stopScanner();
          this.submitResult(decodedText);
        }
      )
      .then(() => {
        this.running = true;
        this.buttonTarget.textContent = "Stop Scan";
      })
      .catch((err) => {
        console.error("Camera error:", err);
        this.showError("Could not access camera");
      });
  }

  async scanFile(event) {
    const file = event.target.files[0];
    if (!file) return;

    this.refreshFileInput();
    this.refreshReaderElement();

    const scanner = new Html5Qrcode(this.readerTarget.id);

    try {
      const result = await scanner.scanFile(file, true);
      this.submitResult(result);
    } catch (err) {
      console.error("File scan error:", err);
      this.showError("Could not read QR from image");
    } finally {
      await scanner.clear();
    }
  }

  refreshFileInput() {
    const oldInput = this.fileTarget;
    const newInput = oldInput.cloneNode(true);
    oldInput.replaceWith(newInput);
  }

  refreshReaderElement() {
    const old = this.readerTarget;
    const fresh = document.createElement("div");
    Array.from(old.attributes).forEach((attr) => {
      fresh.setAttribute(attr.name, attr.value);
    });
    old.replaceWith(fresh);
  }

  submitResult(decodedText) {
    this.clearError();

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ qr_code: decodedText }),
    })
      .then((resp) => {
        if (!resp.ok) {
          return resp.json().then((data) => {
            throw new Error(data.error || "Unknown error");
          });
        }
        return resp.json();
      })
      .then((data) => {
        this.showSuccess(data, decodedText);
      })
      .catch((err) => {
        console.error(err);
        this.showError(err.message);
      });
  }

  async recordAttendance() {
    const qrCode = this.resultNameTarget.dataset.qrCode; // Store QR code from successful scan
    if (!qrCode) return this.showError("No QR code scanned");

    const attendanceUrl =
      this.attendanceButtonTarget.dataset.qrScannerAttendanceUrlValue;

    try {
      const response = await fetch(attendanceUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
        body: JSON.stringify({ qr_code: qrCode }),
      });

      const data = await response.json();
      if (response.ok) {
        window.location.reload();
      } else {
        this.showError(data.error || "Failed to record attendance");
      }
    } catch (err) {
      console.error(err);
      this.showError("Failed to record attendance");
    }
  }

  showSuccess(data, decodedText) {
    this.scannerTarget.classList.add("hidden");
    this.resultNameTarget.textContent = data.profile_name;
    this.resultDistrictTarget.textContent = data.district_full_name;
    this.resultTarget.classList.remove("hidden");

    // Store the QR code for recordAttendance to use
    this.resultNameTarget.dataset.qrCode = decodedText;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  reset() {
    this.resultTarget.classList.add("hidden");
    this.scannerTarget.classList.remove("hidden");
    this.clearError();
    this.buttonTarget.textContent = "Start Scan";
  }

  stopScanner() {
    if (!this.scanner) return;

    return this.scanner.stop().then(() => {
      this.scanner.clear();
      this.running = false;
      this.buttonTarget.textContent = "Start Scan";
    });
  }

  disconnect() {
    this.observer?.disconnect();
    if (this.running) this.stopScanner();
  }
}
